import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { filterProducts, CATEGORIES, TYPES, AUDIENCES } from "../lib/store";
import ProductCard from "../components/ProductCard";
import Seo, { breadcrumbSchema, itemListSchema } from "../components/Seo";
import { CreditNotice } from "../components/CreditNotice";
import { trackViewItemList } from "../lib/analytics";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";

const PER_PAGE = 24;

function FilterGroup({ title, items, active, onPick }) {
  return (
    <div className="mb-7">
      <h4 className="text-xs tracking-[0.2em] uppercase text-[#7a7160] mb-3">{title}</h4>
      <div className="space-y-1">
        {items.map((it) => (
          <button
            key={it.key}
            onClick={() => onPick(active === it.key ? null : it.key)}
            className={`block w-full text-left px-3 py-2 text-sm transition-colors duration-200 ${
              active === it.key
                ? "bg-[#171310] text-[#e9d9ac]"
                : "text-[#3a352c] hover:bg-[#f4eddc]"
            }`}
          >
            {it.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const category = params.get("category");
  const type = params.get("type");
  const audience = params.get("audience");
  const search = params.get("search");
  const sort = params.get("sort") || "featured";

  useEffect(() => { setPage(1); window.scrollTo(0, 0); }, [category, type, audience, search, sort]);

  useEffect(() => { trackViewItemList("shop", 0); }, []);

  const results = useMemo(
    () => filterProducts({ category, type, audience, search, sort }),
    [category, type, audience, search, sort]
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PER_PAGE));
  const pageItems = results.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const setParam = (key) => (val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next);
  };

  const activeFilters = [
    category && { label: CATEGORIES.find((c) => c.key === category)?.label, clear: () => setParam("category")(null) },
    type && { label: TYPES.find((t) => t.key === type)?.label, clear: () => setParam("type")(null) },
    audience && { label: AUDIENCES.find((a) => a.key === audience)?.label, clear: () => setParam("audience")(null) },
    search && { label: `"${search}"`, clear: () => setParam("search")(null) },
  ].filter(Boolean);

  const heading =
    (category && CATEGORIES.find((c) => c.key === category)?.label) ||
    (type && TYPES.find((t) => t.key === type)?.label) ||
    (audience && `For ${AUDIENCES.find((a) => a.key === audience)?.label}`) ||
    (search ? `Search: "${search}"` : "All Jewelry");

  const Filters = (
    <>
      <FilterGroup title="Category" items={CATEGORIES} active={category} onPick={setParam("category")} />
      <FilterGroup title="Jewelry Type" items={TYPES} active={type} onPick={setParam("type")} />
      <FilterGroup title="Shop For" items={AUDIENCES} active={audience} onPick={setParam("audience")} />
    </>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10">
      <Seo
        title={search ? `Search: ${search}` : `${heading} — Fine Jewelry in Chicago`}
        description={`Browse ${results.length} pieces of ${heading.toLowerCase()} at Regal Jewels, Chicago's Devon Avenue jeweler. Filter by category, type and price. Inquire online or on WhatsApp.`}
        path="/shop"
        noindex={Boolean(search)}
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
          ]),
          itemListSchema(pageItems, "/shop"),
        ]}
      />
      {/* Breadcrumb + heading */}
      <p className="text-xs tracking-[0.18em] uppercase text-[#8f8571]">
        <Link to="/" className="hover:text-[#a8842c] transition-colors">Home</Link> / Shop
      </p>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2 mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#171310] tracking-wide">{heading}</h1>
          <p className="text-sm text-[#8f8571] mt-1">{results.length} pieces</p>
          <CreditNotice placement="catalog" className="mt-2" />
        </div>
        <div className="flex items-center gap-3">
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="rounded-none border-[#d8cdb5]">
                  <SlidersHorizontal size={15} className="mr-2" /> Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#fffdf8] w-[290px] overflow-y-auto pt-10">
                {Filters}
              </SheetContent>
            </Sheet>
          </div>
          <Select value={sort} onValueChange={setParam("sort")}>
            <SelectTrigger className="w-[180px] rounded-none border-[#d8cdb5] bg-white">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="price-asc">Price: Low to High</SelectItem>
              <SelectItem value="price-desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {activeFilters.map((f) => (
            <button key={f.label} onClick={f.clear} className="flex items-center gap-1.5 bg-[#171310] text-[#e9d9ac] text-xs tracking-wide px-3 py-1.5 hover:bg-[#3a2f1d] transition-colors duration-200">
              {f.label} <X size={12} />
            </button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-[230px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="hidden lg:block">{Filters}</aside>

        {/* Grid */}
        <div>
          {pageItems.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-heading text-2xl text-[#3a352c]">No pieces found</p>
              <p className="text-sm text-[#8f8571] mt-2">Try clearing filters or a different search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
              {pageItems.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <Button variant="outline" disabled={page === 1} onClick={() => { setPage(page - 1); window.scrollTo(0, 0); }} className="rounded-none border-[#d8cdb5]">Previous</Button>
              <span className="text-sm text-[#5c5240] px-4">Page {page} of {totalPages}</span>
              <Button variant="outline" disabled={page === totalPages} onClick={() => { setPage(page + 1); window.scrollTo(0, 0); }} className="rounded-none border-[#d8cdb5]">Next</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
